import type {SearchField} from 'quickstart/lib/search/config'
import {fieldDepthSps, fieldWantsHits} from 'quickstart/lib/search/hit-counts'
import {runHook} from 'quickstart/lib/search/hooks'
import * as R from 'rambdax'
import {useEffect, useMemo, useState} from 'react'
import {logger, shallowIdentical, sumPairs, truthy} from 'tizra'
import {useApi} from './useApi'
import {useEqualBy} from './useEqualBy'
import type {UseSearchReturn} from './useSearch'
import {useHack} from './useHack'

const log = logger('useHitCounts')

interface UseHitCountsProps {
  search: UseSearchReturn
  field: SearchField
  visible?: boolean
}

/**
 * React hook for fetching hit counts for a given field.
 */
export const useHitCounts = ({
  search,
  field: _field,
  visible = true,
}: UseHitCountsProps) => {
  const picked = useEqualBy(shallowIdentical, {
    ...R.pick(['configured', 'config', 'depths', 'params'], search),
    field: _field,
  })
  const [state, setState] = useState<typeof picked>()

  useEffect(() => {
    if (visible && state !== picked) {
      setState(picked)
    }
  }, [picked, state, visible])

  const {configured, config, depths, params, field} = state || {}
  const wantHits = !!field && fieldWantsHits(field)
  const optimizations = useHack('optimizeHitCounts')

  const sps = useMemo(
    () =>
      wantHits && config && configured && depths && params
        ? fieldDepthSps({config, depths, field, params, optimizations})
        : [],
    [config, configured, depths, field, params, wantHits, optimizations],
  )

  // Make sure to call the hook all four times regardless, following the rules
  // of hooks. (Why four? See useSearch.)
  const queries = [
    useApi.propValuesInfo(sps[0]),
    useApi.propValuesInfo(sps[1]),
    useApi.propValuesInfo(sps[2]),
    useApi.propValuesInfo(sps[3]),
  ].slice(0, sps.length)

  const datas = useEqualBy(
    shallowIdentical,
    queries.map(q => q.data).filter(truthy),
  )

  const loading = wantHits && queries.some(q => q && q.isLoading)

  return useMemo(
    () => ({
      data: wantHits && datas.length ? sumHitCounts({field, datas}) : null,
      loading,
    }),
    [field, datas, loading, wantHits],
  )
}

type ValueCount = Record<string, number>
type CVPairs = {count: number; value: string}[]

/**
 * Repackage ValueCount {v: c, v: c} -> CVPairs [{v, c}, {v, c}]
 */
const toCVPairs = (x: ValueCount): CVPairs =>
  Object.entries(x).map(([value, count]) => ({value, count}))

/**
 * Repackage  CVPairs [{v, c}, {v, c}] -> ValueCount {v: c, v: c}
 */
const toValueCount = (x: CVPairs): ValueCount =>
  Object.fromEntries(x.map(({value, count}) => [value, count]))

function sumHitCounts({
  field,
  datas,
}: {
  field: SearchField
  datas: Record<string, CVPairs>[]
}): ValueCount {
  // Flatten multiple API responses to array of pairs. Some values will have
  // multiple counts because of the multiple API calls.
  const unsummed: CVPairs = datas.flatMap(d => d?.[field.prop!]).filter(truthy)
  // => [{value: 'a', count: 1}, {value: 'a', count: 2}]

  // Reduce the multiple entries for each value into a single entry.
  let summed: ValueCount = sumPairs(unsummed.map(x => [x.value, x.count]))
  // => {value: 'a', count: 3}

  const hook = field.hooks?.hits
  if (hook) {
    summed = R.piped(
      summed,
      toCVPairs,
      hits => runHook(hook, {field, hits}),
      toValueCount,
    )
  }

  return summed
}
